/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import './Collapsible.scss';

/**
 * This is the Collapsible component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Collapsible({
  className,
  component,
  children,
  open,
  onOpen,
  onClosed,
}) {
  const container = useRef(null);
  const content = useRef(null);
  const TagName = component;
  const [height, setHeight] = useState();
  const [ready, setReady] = useState(false);
  const [opened, setOpened] = useState(false);

  useLayoutEffect(() => {
    let timeout = null;
    function onResize() {
      if (!open) {
        setReady(false);
      }

      setHeight(content.current.offsetHeight);
    }
    window.addEventListener('resize', onResize);
    timeout = setTimeout(onResize, 200);
    return function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  useLayoutEffect(() => {
    let timeout = null;
    if (!ready && height) {
      timeout = setTimeout(() => {
        setReady(true);
      }, 160);
    }
    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [height, ready]);

  useLayoutEffect(() => {
    let timeout = null;
    const { current } = container;
    if (height) {
      if (open) {
        current.style.height = `${height}px`;
      } else {
        current.style.height = `${height}px`;
        timeout = setTimeout(() => {
          current.style.height = 0;
        }, 160);
      }
    }

    return function cleanup() {
      clearTimeout(timeout);
    };
  }, [height, open]);

  useEffect(() => {
    if (opened) {
      onOpen({ target: container.current });
    } else {
      onClosed({ target: container.current });
    }
  }, [opened, onOpen, onClosed]);

  return (
    <div
      ref={container}
      className={classnames('collapsible', className, { init: !ready })}
      onTransitionEnd={({ target }) => {
        if (target === container.current) {
          setOpened(open);
        }
      }}
    >
      <TagName ref={content}>{children}</TagName>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Collapsible.propTypes = {
  className: PropTypes.string,
  component: PropTypes.string.isRequired,
  children: PropTypes.any,
  open: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onClosed: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Collapsible.defaultProps = {
  component: 'div',
  open: false,
  onOpen: () => {},
  onClosed: () => {},
};

export default Collapsible;
